import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BellRinging, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader, Th, Td, Empty, Kpi } from "@/components/common";
import { api, money, fmtDate, fmtDateTime, errMsg } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import NoteReminders from "@/components/NoteReminders";

export default function Reminders() {
  const nav = useNavigate();
  const [data, reload] = useLoad(() => api.get("/reminders").then((r) => r.data), []);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const rows = data?.rows || [];
  const sendable = rows.filter((r) => r.email);
  const allSel = sendable.length > 0 && sel.length === sendable.length;

  const send = async (ids) => {
    setBusy(true);
    try {
      const r = await api.post("/reminders/send", { ids });
      const { sent, failed } = r.data;
      if (sent.length) toast.success(`${sent.length} hatırlatma e-postası gönderildi`);
      failed.forEach((f) => toast.error(`${f.no || f.id}: ${f.reason}`));
      setSel([]);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div data-testid="reminders-page">
      <PageHeader eyebrow="Tahsilat Takibi" title="Vade Hatırlatma">
        {sel.length > 0 && (
          <Button onClick={() => send(sel)} disabled={busy} className="rounded-[4px] bg-teal-700 hover:bg-teal-800" data-testid="reminders-send-selected-button">
            <PaperPlaneTilt /> Seçilenlere Gönder ({sel.length})
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Kpi label="Hatırlatılacak Fatura" value={data ? String(data.count) : "…"} testid="reminders-count" />
        <Kpi label="Toplam Açık Tutar" value={data ? money(data.total) : "…"} tone="danger" testid="reminders-total" />
        <Kpi label="Bugün Vadesi Dolan" value={data ? String(rows.filter((r) => r.days === 0).length) : "…"} testid="reminders-due-today" />
        <Kpi label="Yaklaşan Çek / Senet" value={data ? String((data.notes || []).length) : "…"} sub={data ? money(data.notes_total || 0) : ""} tone="amber" testid="reminders-notes-count" />
      </div>

      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr>
              <Th><input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : sendable.map((r) => r.id))} className="h-4 w-4 accent-teal-700" data-testid="reminders-select-all" /></Th>
              <Th>No</Th><Th>Cari</Th><Th>Vade</Th><Th>Durum</Th><Th right>Açık Tutar</Th><Th>E-posta</Th><Th>Son Gönderim</Th><Th />
            </tr>
          </thead>
          <tbody>
            {data && rows.length === 0 && <Empty cols={9} />}
            {rows.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-muted/50" data-testid={`reminder-row-${r.no}`}>
                <Td title={!r.email ? "E-posta tanımlı değil" : undefined}>
                  <input type="checkbox" disabled={!r.email} checked={sel.includes(r.id)} className="h-4 w-4 accent-teal-700"
                    onChange={() => setSel((s) => (s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id]))}
                    data-testid={`reminder-select-${r.no}`} />
                </Td>
                <Td><button className="font-num whitespace-nowrap underline-offset-2 hover:underline" onClick={() => nav(`/faturalar/${r.id}`)} data-testid={`reminder-open-${r.no}`}>{r.no}</button></Td>
                <Td>{r.account_name}</Td>
                <Td className="font-num whitespace-nowrap">{fmtDate(r.due_date)}</Td>
                <Td>
                  <span className={`inline-block whitespace-nowrap px-2 py-0.5 text-[11px] font-semibold ${r.days === 0 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
                    {r.days === 0 ? "Vadesi bugün" : `${r.days} gün gecikti`}
                  </span>
                </Td>
                <Td right>{money(r.open_amount)}</Td>
                <Td className="text-xs">{r.email || <span className="text-red-600">tanımlı değil</span>}</Td>
                <Td className="text-xs text-muted-foreground" data-testid={`reminder-last-sent-${r.no}`}>
                  {r.reminded_at ? <span className="inline-flex items-center gap-1 text-teal-700"><CheckCircle size={14} weight="fill" /> {fmtDateTime(r.reminded_at)}</span> : "—"}
                </Td>
                <Td>
                  <Button size="sm" variant="outline" disabled={!r.email || busy} className="rounded-[4px]" onClick={() => send([r.id])} data-testid={`reminder-send-${r.no}`}>
                    <BellRinging size={14} /> Hatırlat
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">Vadesi bugün dolan ve 7 gün geciken açık satış faturaları listelenir. Gönderim yalnızca siz tetiklediğinizde yapılır.</p>
      {data && <NoteReminders notes={data.notes || []} total={data.notes_total || 0} onSent={reload} />}
    </div>
  );
}
